(ns ductile.ui.util
  (:require ["@turf/turf" :as turf]
            ["luxon" :refer [DateTime]]
            [clojure.edn :as edn]
            [clojure.string :as str]
            [ductile.base64u :as base64]
            [ductile.ui.deframe :as deframe]
            [ductile.ui.locale :as locale]
            [goog.string :as gstring]
            [nextjournal.log :as log]
            [reitit.impl :as reitit]))

(defn has-required-scope? [scope]
  (some #{scope}
        (:user/scopes @deframe/!current-user)))

(defn has-required-write-scope? [scope]
  (some #{scope}
        (:user/write-scopes @deframe/!current-user)))

(defn current-compound-name []
  @deframe/!current-compound)

(defn no-error? [{:keys [body]}]
  (not (contains? body :error)))

(defn full-vin? [s] ;; TODO dedupe with backend fn
  (boolean (re-matches #"^[A-Z0-9]{17}$" s)))


(def email-regex #"^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,63}$")

(defn meta-value [name]
  (if (exists? js/document)
    (some-> js/document
            (.querySelector (str "meta[name='" name "']"))
            (.getAttribute "content"))
    "react-native-inert-token")) ;; avoids warning

(defn csrf-token []
  (meta-value "csrf-token"))

;; duplicated from clj util
(defn dissoc-blank-vals
  "Remove map values that are `nil?` or `string?` and blank."
  [m]
  (into (empty m)
        (remove #(or (nil? (val %))
                     (and (string? (val %))
                          (str/blank? (val %)))))
        m))


(defn render-iso-localdatetime [localdatetime]
  ;; e.g. `2020-07-06T12:16`
  (when (string? localdatetime)
    (str (subs localdatetime 8 10) "."
         (subs localdatetime 5 7) "."
         (subs localdatetime 0 4) ", "
         (subs localdatetime 11 13) ":"
         (subs localdatetime 14 16))))

(def date-input-zone "Europe/Berlin")

(defn inst->date-input-value
  ([inst] (inst->date-input-value inst date-input-zone))
  ([inst zone]
   (when (inst? inst)
     (.toISODate (.fromJSDate DateTime inst #js {:zone zone})))))

(defn date-input-value->inst
  ([s] (date-input-value->inst s date-input-zone))
  ([s zone]
   (when (seq s)
     (.toJSDate (.startOf (.fromISO DateTime s #js {:zone zone}) "day")))))

(defn parse-query-params
  ([query-params] (parse-query-params query-params #{}))
  ([query-params exclude-keys]
   (if-let [b64 (:b64 query-params)]
     (apply dissoc (edn/read-string (base64/decode b64)) exclude-keys)
     (into {}
           (map (fn [[k v]] (cond
                              (and (str/blank? (name k)) (str/blank? v))
                              nil

                              (and (not (contains? exclude-keys k))
                                   (string? v))
                              [k (edn/read-string v)]
                              :else
                              [k v])))
           query-params))))

(defn map->url-encoded-query-params [m]
  (str/join
   "&"
   (map (fn [[k v]]
          (when v (str (reitit/url-encode (subs (str k) 1)) "=" (reitit/url-encode v))))
        m)))

(defn set-query-params! [m]
  (.pushState js/window.history nil nil (str "?" (map->url-encoded-query-params m)))
  m)

(defn format-price
  ([price]
   (format-price price :currency/euro))
  ([price currency]
   (let [price-float (/ price 100)
         currency-str (case currency
                        :currency/euro "EUR"
                        :currency/dollar "USD"
                        "EUR")]
     (.format (js/Intl.NumberFormat. (locale/get-current-iso-locale)
                                     #js {:style "currency"
                                          :currency currency-str})
              price-float))))

(defn find-by-lookup-ref [entities lookup-ref]
  (when lookup-ref
    (some (fn [entity]
            (when (= (second lookup-ref) (get entity (first lookup-ref)))
              entity))
          entities)))

(defn lookup-ref? [x]
  (and (vector? x)
       (= 2 (count x))
       (keyword (first x))))

(defn ->lookup-ref [x]
  (if (lookup-ref? x)
    x
    (do (when-not (:ductile/id x)
          (throw (ex-info "missing :ductile/id" {:entity x})))
        [:ductile/id (:ductile/id x)])))

(defn index-of [x xs]
  (loop [i 0 xs xs]
    (cond
      (nil? xs) nil
      (= (first xs) x) i
      :else (recur (inc i) (next xs)))))

(defn str-join-existing [separator & values]
  (str/join separator (remove nil? (into [] values))))

(defn absolute-difference ^double [^double x ^double y]
  (Math/abs (double (- x y))))

(defn close? [tolerance a b]
  (< (absolute-difference a b) tolerance))

(defn format-distance [distance]
  (str (gstring/format "%.2f" (/ distance 1000)) " km"))

(defn format-duration [s]
  (str (js/Math.floor (/ s 3600)) "h " (rem (js/Math.floor (/ s 60)) 60) "min"))

(defn vec-remove [coll pos]
  (vec (concat (subvec coll 0 pos) (subvec coll (inc pos)))))

(defn vec-add [coll pos el]
  (concat (subvec coll 0 pos) [el] (subvec coll pos)))

(defn vec-move [coll pos1 pos2]
  (let [el (nth coll pos1)]
    (if (= pos1 pos2)
      coll
      (into [] (vec-add (vec-remove coll pos1) pos2 el)))))

(defn distance-sort [origin-coord a b]
  (let [origin-point (turf/point (clj->js (if (every? nil? origin-coord) [0.0 0.0] origin-coord)))
        a' (if (every? nil? a) [0.0 0.0] a)
        b' (if (every? nil? b) [0.0 0.0] b)]
    (- (turf/distance origin-point (turf/point (clj->js a')) #js {:units "radians"})
       (turf/distance origin-point (turf/point (clj->js b')) #js {:units "radians"}))))

(defn update-when [m k f & args]
  (if (contains? m k)
    (apply update m k f args)
    m))

(defn compare-with-nil-last [l r]
  (cond
    (and (nil? l) (nil? r)) 0
    (nil? l)                1
    (nil? r)                -1
    (and (sequential? l)
         (sequential? r))   (let [comparison-first (compare-with-nil-last (first l) (first r))]
                              (if (zero? comparison-first)
                                (compare-with-nil-last (rest l) (rest r))
                                comparison-first))
    :else                   (compare l r)))

(def osm-opening-hours-day-token-regex #"\b(?:Mo|Tu|We|Th|Fr|Sa|Su|PH|SH)\b")

(def osm-opening-hours-day-translations
  {:en {"Mo" "Mo"
        "Tu" "Tu"
        "We" "We"
        "Th" "Th"
        "Fr" "Fr"
        "Sa" "Sa"
        "Su" "Su"
        "PH" "PH"
        "SH" "SH"}
   :de {"Mo" "Mo"
        "Tu" "Di"
        "We" "Mi"
        "Th" "Do"
        "Fr" "Fr"
        "Sa" "Sa"
        "Su" "So"
        "PH" "FT"
        "SH" "SF"}
   :it {"Mo" "Lu"
        "Tu" "Ma"
        "We" "Me"
        "Th" "Gi"
        "Fr" "Ve"
        "Sa" "Sa"
        "Su" "Do"
        "PH" "FP"
        "SH" "SC"}})

(defn translate-opening-hours-day-components [opening-hours]
  (if (string? opening-hours)
    (let [locale (locale/get-current-locale)
          day-map (get osm-opening-hours-day-translations locale
                       (get osm-opening-hours-day-translations :en))]
      (str/replace opening-hours
                   osm-opening-hours-day-token-regex
                   (fn [day-token]
                     (get day-map day-token day-token))))
    opening-hours))
